import {IScheduleState} from './types';
import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import {DiaryRootState} from 'appRoot/rootReducer';
import PouchLoadService from 'external-services/pouch-load-service';
import {
  IDiarySchedule,
  IDiaryScheduleRawTimes,
  IPouchRows,
  IScheduleLookup,
  IServiceItem,
  loadStatus,
  PouchDocType,
} from 'shared-types/index';
import {getServiceTimesFromSelectedService, serviceActions} from 'app/reducers/service/slice';
import DateUtilsService from 'external-services/date-utils-service';
import ScheduleLookupService from 'external-services/schedule-lookup-service/scheduleLookup.service';
import ServicesLookupService from 'external-services/services-lookup-service';
import SectionLookupService from 'external-services/section-lookup-service/sectionLookup.service';
import {defineAvailableSections} from 'app/reducers/section/slice';
import {errorStateActions, getAccordionDetail} from 'app/reducers/errorState/slice';
import {CONTACT_MSG, PROBLEMS_MSG, REFRESH_MSG} from 'appRoot/app-error-messages';
import {RejectedWithValueActionFromAsyncThunk} from '@reduxjs/toolkit/dist/matchers';
import {cloneDeep, sortBy} from "lodash-es";
import PouchActions from 'app/actions/pouch.actions';
import {AppDispatch} from 'appRoot/Store';
import {FloorplanV2State} from 'floorplan-v2/app/appRoot/rootReducer';
import ScheduleExtensionService from 'external-services/schedule-extension-service';
import {updateFloorplanBlockouts} from 'floorplan-v2/app/reducers/blockouts/slice';
import NotesService from 'external-services/notes-service';

const NS = 'scheduleSlice';

const initialState: IScheduleState = {
  couchReducedSchedules: null,
  selectedSchedule: null,
  scheduleLookup: {},
  selectedDate: new Date(), // @todo: should be using venue time
  selectedScheduleLoadingStatus: loadStatus.idle,
  venueNotes: '',
  hiddenServices: []
};
export const blockoutPouchListener = (dispatch: AppDispatch) => {
  return PouchActions.handlePouchSyncedDoctype(
    PouchDocType.blockout,
    (evt, payload) => {
      dispatch(updateBlockoutsForSelectedSchedule());
    }
  );
}
export const updateBlockoutsForSelectedSchedule = createAsyncThunk(
  `schedule/updateBlockoutsForSelectedSchedule`,
  async (payload: void, { dispatch, getState }): Promise<any> => {
    const {
      scheduleReducer: { selectedSchedule, selectedDate },
    } = getState() as FloorplanV2State;
    const schedule = await ScheduleExtensionService.addBlockouts(cloneDeep(selectedSchedule), selectedDate);
    dispatch(updateFloorplanBlockouts(schedule.blockouts));
    return schedule;
  }
);
// to avoid duplicate schedules with the same dayName
const cleanSchedules = (schedules: IDiaryScheduleRawTimes[]): IDiaryScheduleRawTimes[] => {
  const cleanSchedules: IDiaryScheduleRawTimes[] = [];
  const indexSet = new Set();
  for (let i = schedules.length - 1; i >= 0; i--) {
    if (!indexSet.has(schedules[i].dayName)) {
      indexSet.add(schedules[i].dayName);
      cleanSchedules.push(schedules[i]);
    }
  }
  return cleanSchedules;
};
export const loadVenueNotes = createAsyncThunk(
  'schedule/loadVenueNotes',
  async (payload: Date, { rejectWithValue }): Promise<string> => {
    let notes: string = '';
    try {
      const res= await NotesService.getNote(payload)
      notes = res.note;
    } catch (e: any) {
      console.log(NS, 'venue notes err', e);
    }
    return notes;
  }
);
export const loadSchedules = createAsyncThunk(
  'schedule/loadSchedules',
  async (
    payload: void,
    { rejectWithValue, dispatch, getState }
  ): Promise<IDiaryScheduleRawTimes[] | RejectedWithValueActionFromAsyncThunk<any>> => {
    let schedules: IDiaryScheduleRawTimes[] = null;
    try {
      const data: IPouchRows<IDiaryScheduleRawTimes> = (await PouchLoadService.getDbLocal().allDocs(
        {
          include_docs: true,
          attachments: false,
          startkey: 'schedule_day',
          endkey: 'schedule_day' + '\uffff',
        }
      )) as unknown as IPouchRows<IDiaryScheduleRawTimes>;
      schedules = data?.rows.map((r) => r.doc) || null;
    } catch (e: any) {
      console.log(NS, 'schedules err', e);
      return rejectWithValue(e.message);
    }

    return cleanSchedules(schedules);
  }
);


export const addToScheduleLookup = createAsyncThunk(
  'schedule/addToScheduleLookup',
  async (
    payload: { schedule: IDiarySchedule; date: Date },
    { dispatch, getState }
  ): Promise<{
    scheduleLookup: IScheduleLookup;
  }> => {
    const { scheduleReducer } = getState() as DiaryRootState;

    const scheduleLookup: IScheduleLookup = { ...scheduleReducer.scheduleLookup };

    const dateId: string = DateUtilsService.dateToIdStr(payload.date);
    if (!scheduleLookup[dateId]) {
      scheduleLookup[dateId] = {
        schedule: payload.schedule,
        dateAdded: new Date(),
      };
    }

    return { scheduleLookup };
  }
);

export const setSelectedSchedule = createAsyncThunk(
  'schedule/setSelectedSchedule',
  async (
    {useCachedScheduleLookup = true} : { useCachedScheduleLookup: boolean},
    { dispatch, getState }
  ): Promise<{
    selectedSchedule: IDiarySchedule;
    scheduleLookup: IScheduleLookup;
  }> => {
    const { scheduleReducer } = getState() as DiaryRootState;

    dispatch(actions.setSelectedScheduleLoadingStatus(loadStatus.loading));

    // if venue changed then load the schedule from scratch
    const scheduleLookup: IScheduleLookup = useCachedScheduleLookup ? { ...scheduleReducer.scheduleLookup } : {};
    let selectedSchedule: IDiarySchedule;
    try {
      selectedSchedule = await ScheduleLookupService.getSchedule(
        scheduleReducer.selectedDate,
        scheduleLookup,
        false
      );
      selectedSchedule = selectedSchedule ? cloneDeep(selectedSchedule) : null;
    } catch (err) {
      console.warn(NS, 'ScheduleLookupService.getSchedule failed', err);

      setTimeout(() => {
        dispatch(
          errorStateActions.setErrorState({
            errorTitle: 'Problem loading the "Schedule"',
            errorMessage: `There was an error trying to load data for the "schedule" on that day. \n\n${PROBLEMS_MSG} ${REFRESH_MSG} \n\n${CONTACT_MSG}`,
            accordionDetail: getAccordionDetail(err),
          })
        );
      }, 1000);
    }

    if (selectedSchedule) {
      const servicesAndServiceGroup = await ServicesLookupService.getServicesAndServiceGroupsAsync(
        selectedSchedule
      );
      const serviceViewList = ServicesLookupService.getTrueServicesOrder(sortBy(servicesAndServiceGroup, 'order'));
      if (serviceViewList) {
        const times = serviceViewList.map((serviceView) => ((serviceView.service as IServiceItem)?.endTime as Date)?.getTime()).filter((value) => !!value)
        const latestTimeOfTheDay = Math.max(...times);
        dispatch(serviceActions.setLatestTimeOfTheDay(new Date(latestTimeOfTheDay)));
        dispatch(serviceActions.setCurrentServiceViews(serviceViewList));
        dispatch(serviceActions.setLatestTimeOfTheDayBySection(serviceViewList));
      }
      if (selectedSchedule.services && selectedSchedule.isOpen) {
        if (selectedSchedule.services && selectedSchedule.services.length) {
          const defSecLOs = SectionLookupService.getDefaultSectionLayouts(selectedSchedule);

          const sectionIds = defSecLOs.sectionIds;
          // const defaultSectionLayouts = defSecLOs.defaultSectionLayouts;
          await dispatch(defineAvailableSections({ sectionIds: sectionIds }));
        }
      }
    } else {
      dispatch(serviceActions.setCurrentServiceViews([]));
    }
    return { selectedSchedule, scheduleLookup: cloneDeep(scheduleLookup) };
  }
);

const scheduleSlice = createSlice({
  name: 'schedule', // used in action types
  initialState,
  reducers: {
    setSelectedDate: (state: IScheduleState, action: PayloadAction<Date>) => {
      state.selectedDate = action.payload;
    },
    setVenueNotes: (state: IScheduleState, action: PayloadAction<string>) => {
      state.venueNotes = action.payload;
    },
    setHiddenServices: (state: IScheduleState, action: PayloadAction<string[]>) => {
      state.hiddenServices = action.payload;
    },
    setSelectedScheduleLoadingStatus: (state: IScheduleState, action: PayloadAction<loadStatus>) => {
      state.selectedScheduleLoadingStatus = action.payload;
    },
    clearScheduleLookUp: (state: IScheduleState) => {
      state.scheduleLookup =  {};
    },
    removeBlockout: (state: IScheduleState, action: PayloadAction<{date: Date, id: string}>) => {
      const { date, id } = action.payload;
      const dateStr: string = DateUtilsService.dateToIdStr(date);
      if (state.scheduleLookup[dateStr]) {
        const selectedSchedule = state.scheduleLookup[dateStr].schedule
        const filteredBlockouts = selectedSchedule.blockouts.filter(({ _id }) => _id !== id);
        selectedSchedule.blockouts = filteredBlockouts
        state.scheduleLookup = {
          ...state.scheduleLookup,
          [dateStr]: {
            ...state.scheduleLookup[dateStr],
            schedule: selectedSchedule
          }
        }
      }
    },
    reset: () => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadSchedules.fulfilled, (state: IScheduleState, action) => {
        state.couchReducedSchedules = action.payload as IDiaryScheduleRawTimes[];
      })
      .addCase(updateBlockoutsForSelectedSchedule.fulfilled, (state: IScheduleState, action) => {
        state.selectedSchedule = action.payload;
      })
      .addCase(loadVenueNotes.fulfilled, (state: IScheduleState, action) => {
        state.venueNotes = action.payload;
      })
      .addCase(setSelectedSchedule.fulfilled, (state: IScheduleState, action) => {
        state.selectedSchedule = action.payload.selectedSchedule;
        state.scheduleLookup = action.payload.scheduleLookup;
        state.selectedScheduleLoadingStatus = loadStatus.success;
      })
      .addCase(addToScheduleLookup.fulfilled, (state: IScheduleState, action) => {
        state.scheduleLookup = action.payload.scheduleLookup;
      });
  },
});

// Extract the action creators object and the reducer
const { actions, reducer } = scheduleSlice;
// Extract and export each action creator by name
export { actions as scheduleActions };
// Export the reducer, either as a default or named export
export default reducer;
